"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

export default function ContactForm() {
  const searchParams = useSearchParams();
  const subjectFromQuery = searchParams.get("subject") || "";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: subjectFromQuery,
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, subject: subjectFromQuery }));
  }, [subjectFromQuery]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const res = await fetch("https://cms.travelalvaro.sk/form/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (data.status === "ok") {
        if (typeof window !== "undefined") {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "travel_contact_form_submitted",
            event_id: "contact_" + Date.now(),
            method: "contact_form",
            user_email: formData.email || null,
            value: 0,
            currency: "EUR",
            subject: formData.subject || null,
          });
        }
        fetch("/api/form/contact", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });

        setSubmitted(true);
      } else {
        alert("Chyba pri odosielaní: " + data.message);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Chyba pri odosielaní formulára. Skúste to prosím znovu.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className='mt-6 text-center'>
        <h2 className='text-2xl font-bold mb-2'>Ďakujeme!</h2>
        <p>Vaša správa bola úspešne odoslaná.</p>
      </div>
    );
  }

  return (
    <div className='bg-[#F5F5F5] mb-20 text-left p-4 rounded-xl w-full max-w-4xl mx-auto'>
      <form onSubmit={handleSubmit}>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div className='flex flex-col'>
            <label htmlFor='name' className='mb-1'>
              Meno*
            </label>
            <input
              id='name'
              name='name'
              type='text'
              value={formData.name}
              onChange={handleChange}
              required
              className='border border-[#D9D9D9] bg-[#FFFFFF] rounded-md p-2 w-full'
              placeholder='Zadaj svoje meno'
            />
          </div>

          <div className='flex flex-col'>
            <label htmlFor='email' className='mb-1'>
              Email*
            </label>
            <input
              id='email'
              name='email'
              type='email'
              value={formData.email}
              onChange={handleChange}
              required
              className='border border-[#D9D9D9] bg-[#FFFFFF] rounded-md p-2 w-full'
              placeholder='Zadaj svoj email'
            />
          </div>

          <div className='flex flex-col'>
            <label htmlFor='subject' className='mb-1'>
              Predmet*
            </label>
            <input
              id='subject'
              name='subject'
              type='text'
              value={formData.subject}
              onChange={handleChange}
              required
              className='border border-[#D9D9D9] bg-[#FFFFFF] rounded-md p-2 w-full'
              placeholder='Zadaj predmet správy'
            />
          </div>

          <div className='flex flex-col md:col-span-3'>
            <label htmlFor='message' className='mb-1'>
              Vaša správa*
            </label>
            <textarea
              id='message'
              name='message'
              value={formData.message}
              onChange={handleChange}
              required
              rows={5}
              className='border border-[#D9D9D9] bg-[#FFFFFF] rounded-md p-2 w-full'
              placeholder='Zadaj text správy'
            />
          </div>
        </div>

        <div className='flex flex-col items-center gap-4 text-xs justify-center mt-4'>
          <div className='flex items-start gap-2 text-xs md:text-center lg:justify-start'>
            <input
              type='checkbox'
              onInvalid={(e: React.FormEvent<HTMLInputElement>) =>
                e.currentTarget.setCustomValidity(
                  "Prosím, zaškrnite súhlas, ak chcete pokračovať v objednávke."
                )
              }
              onInput={(e: React.FormEvent<HTMLInputElement>) =>
                e.currentTarget.setCustomValidity("")
              }
              required
              className='appearance-none h-4 w-4 border border-gray-400 bg-white rounded-full checked:bg-[#00638A] checked:border-white checked:border-[3px] checked:outline-[#00638A] checked:outline-1 shrink-0'
            />
            <label htmlFor='terms' className='text-left'>
              Súhlasím so spracovaním{" "}
              <Link
                href='/dokumenty-na-stiahnutie'
                className='underline text-orange-400'
              >
                <span className='text-orange-400'>osobných údajov</span> a
                <span className='text-orange-400'> obchodnými podmienkami</span>
                .
              </Link>
            </label>
          </div>
          <button
            type='submit'
            disabled={loading}
            aria-busy={loading}
            className='bg-[#619F1C] text-white cursor-pointer text-sm py-3 rounded-full font-semibold hover:bg-[#c65c1e] transition w-38 disabled:opacity-60 disabled:cursor-not-allowed'
          >
            {loading ? (
              <span className='flex items-center justify-center gap-2'>
                <svg
                  className='animate-spin h-4 w-4 text-white'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z'
                  ></path>
                </svg>
                Odosielam...
              </span>
            ) : (
              "Odoslať"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
